import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import CloseIcon from "../../../assets/images/close_icon.svg";
import "../DataEntryDetails.scss";
import {
    postQATransactionIssueActionCreators,
} from "../DataEntryDetailsDucks";
import { ProceedIcon } from "../../../assets/images/proceedIcon";
import TransactionIssue, {QaError} from "../../../models/transactionIssue";
import { User } from "../../../models/misc";
import TransactionDocument from "../../../models/document";
import { DocTypes } from "../../../common/constants/DocTypes";

interface IpropType {
    close: () => void,
    qaErrors: QaError[],
    userAttributes?: any,
    postQATransactionIssue?: any,
    transactionId?: string,
    documentsList: readonly TransactionDocument[],
    editIssue?: TransactionIssue,
    auditUsers?: User[],
}

const AddIssueForm = (props:IpropType) => {
    const [formValues, setFormValues] = useState({
      qaErrorID: '',
      pageNumber: '',
      issueDetails: '',
      madeBy: "",
    });

    const [errors, setErrors] = useState({
      qaErrorID: '',
      pageNumber: '',
      issueDetails: '',
      madeBy: "",
    });

    const releaseDocument = props.documentsList.find(doc => doc.type.role == DocTypes.QA || doc.type.role == DocTypes.RELEASE);
    const maxPage = releaseDocument?.pageCount ? releaseDocument.pageCount : 9999;
    const errorType = props.qaErrors.find(err => err.qaErrorId == parseInt(formValues.qaErrorID))?.qaErrorTypeName;

    useEffect(() =>{
      //type dropdown
      const dropdown = document.getElementById("issue-type") as HTMLSelectElement;
      if (dropdown) {
        dropdown.value = props.editIssue?.qaErrorID.toString() ?? "";
      }

      //page
      const page = document.getElementById("page-number") as HTMLInputElement;
      if (page) {
        page.value = props.editIssue?.pageNumber?.toString() ?? "";
      }

      //details
      const details = document.getElementById("issue-details") as HTMLTextAreaElement;
      if (details) {
        details.value = props.editIssue?.issueDetails ?? "";
      }

      const madeBy = document.getElementById("issue-made-by") as HTMLSelectElement;
      if (madeBy) {
        madeBy.value = props.editIssue?.madeBy.id.toString() ?? "";
      }

      setFormValues({
        qaErrorID: dropdown.value,
        pageNumber: page.value,
        issueDetails: details.value,
        madeBy: madeBy.value,
      });
    }, [props.editIssue?.issueID]);
  
    const handleChange = (name: string, value: string) => {
      if (name === 'pageNumber') {
        if (parseInt(value, 10) > maxPage) {
          value = maxPage.toString();
            setFormValues({ ...formValues, [name]: maxPage.toString() });
        } else if (parseInt(value, 10) <= 0) { 
          setFormValues({ ...formValues, [name]: "1" });
        } else {
            setFormValues({ ...formValues, [name]: value });
        }
      }
      setFormValues({ ...formValues, [name]: value });
      setErrors({...errors, [name]: ''});
    };
  
    const onClickSaveIssue = async () => {
      if (releaseDocument == null) {
        return;
      }
      let newErrors = { qaErrorID: '', pageNumber: '', issueDetails: '', madeBy: "" };

      let valid = true;

      const pageNumber = parseInt(formValues.pageNumber, 10);

      if (!formValues.qaErrorID) {
        newErrors.qaErrorID = "Select valid Issue Type.";
        valid = false;
      } else if ((isNaN(pageNumber) || pageNumber <= 0 || pageNumber > maxPage) && errorType == "PD") {
        newErrors.pageNumber = "Invalid page number, must be between 1-" + maxPage + ".";
        valid = false;
      }
      if (!formValues.issueDetails || formValues.issueDetails.match(/^ *$/) !== null) {
        newErrors.issueDetails = "You must enter valid details about the issue.";
        valid = false;
      }
      if (!formValues.madeBy) {
        newErrors.madeBy = "Select the user who made the error.";
        valid = false;
      }

      if (valid) {
        await props.postQATransactionIssue(
          parseInt(props.transactionId!.split("-")[0]),
          parseInt(props.transactionId!.split("-")[1]),
          parseInt(formValues.qaErrorID),
          formValues.issueDetails,
          releaseDocument.file.name,
          parseInt(formValues.madeBy),
          pageNumber || undefined,
          props.editIssue?.issueID
        );

        setFormValues({
          qaErrorID: '',
          pageNumber: '',
          issueDetails: '',
          madeBy: "",
        });
        props.close()
      } else {
        setErrors(newErrors);
      }
    };
  
    const validatePageNumberEvent = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (["e", "E", "+", "-", "."].includes(e.key)) {
        e.preventDefault();
      }
    };
  
    const validatePastedPageNumber = (e: React.ClipboardEvent<HTMLInputElement>) => {
      const clipboardData = e.clipboardData;
      const pastedData = clipboardData.getData('text');
      if (pastedData.includes('e') || pastedData.includes('E') || pastedData.includes('+') || pastedData.includes('-') || pastedData.includes('.')) {
        e.preventDefault();
      }
    };
  
    return (
      <div id="add-issue-form" className="add-issue-form">
        <div className="d-flex flex-row">
          <span className="static-text flex-grow-1" id="add-issue-header">
            {props.editIssue ? "Edit Issue" : "Add Issue"}
          </span>
          <button type="button" id="cancel-issue" onClick={props.close} style={{padding: "0px !important"}}>
            <img src={CloseIcon} width={17} height={14} alt="Cancel" style={{
              marginTop: "auto",
              marginBottom: "auto",
              display: "block",
            }}/>
          </button>
        </div>
  
        <div className="static-text">By: {props.userAttributes?.firstName} {props.userAttributes?.lastName}</div>

        <div className="d-flex flex-row justify-content-stretch">
          <div className="left-issue">
            <label className="form-label" htmlFor="issue-type">Issue Type</label>
            <select
              className={`input-field form-control ${errors.qaErrorID ? 'is-invalid' : ''}`}
              id="issue-type"
              name="qaErrorID"
              onChange={event => handleChange(event.target.name, event.target.value)}
              value={formValues.qaErrorID}
              disabled={props.editIssue?.disputed}
              required
            >
              <option id="select-placeholder" value="" disabled hidden>Select Issue Type</option>
              {props.qaErrors.map((errorType) => (
                <option key={`qaError${errorType.qaErrorId}`} value={errorType.qaErrorId}>
                  ({errorType.qaErrorTypeName}) {errorType.qaErrorName}
                </option>
              ))}
            </select>
          </div>
  
          <div className="right-issue">
            <label className="form-label" htmlFor="page-number">Page Number</label>
            <input
              className={`input-field form-control ${errors.pageNumber ? 'is-invalid' : ''}`}
              id="page-number"
              name="pageNumber"
              max={maxPage}
              type="number"
              placeholder="Page #"
              min="1"
              value={formValues.pageNumber}
              onKeyDown={validatePageNumberEvent}
              onPaste={validatePastedPageNumber}
              onChange={event => handleChange(event.target.name, event.target.value)}
              disabled={props.editIssue?.disputed}
              required={errorType == "PD"}
            />
          </div>
        </div>
        {errors.qaErrorID && <div className="invalid-feedback">{errors.qaErrorID}</div>}
        {errors.pageNumber && <div className="invalid-feedback">{errors.pageNumber}</div>}
        <div>
          <label className="form-label" htmlFor="issue-details">Issue Details</label>
          <textarea
            className={`input-field form-control ${errors.issueDetails ? 'is-invalid' : ''}`}
            style={{height: "120px"}}
            id="issue-details"
            name="issueDetails"
            placeholder="Enter details here..."
            onChange={event => handleChange(event.target.name, event.target.value)}
            value={formValues.issueDetails}
            disabled={props.editIssue?.disputed}
            required
          />
        </div>
        {errors.issueDetails && <div className="invalid-feedback">{errors.issueDetails}</div>}
        <div>
          <label className="form-label" htmlFor="issue-made-by">Issue Made By</label>
          <select
            id="issue-made-by"
            className={`input-field form-control ${errors.madeBy ? 'is-invalid' : ''}`}
            name="madeBy"
            onChange={event => handleChange(event.target.name, event.target.value)}
            value={formValues.madeBy}
            required
          >
            <option value="" disabled hidden>Select User</option>
            {props.auditUsers?.map(x => <option key={`audituser${x.id}`} value={x.id}>{ x.displayName }</option>)}
          </select>
        </div>
        {errors.madeBy && <div className="invalid-feedback">{errors.madeBy}</div>}
        <div className="d-flex justify-content-end align-items-middle" style={{gap: "21px"}}>
          <button type="button" onClick={props.close}><a>Cancel</a></button>
          <button type="button" className="btn" onClick={_event => onClickSaveIssue()}>
            <ProceedIcon color='white' /> Save Issue
          </button>
        </div>
      </div>
    );
};

function mapStateToProps(state: any) {
  return {
    auditUsers: state.dataEntryState.auditUsers,
  };
}

function mapDispatchToProps(dispatch: Function) {
  return {
    postQATransactionIssue: (organizationId: number, requestBufferId: number, errorId: number, details: string, filename: string, madeBy: number, pageNumber?: number, editIssueId?: string) =>
      dispatch(postQATransactionIssueActionCreators(organizationId, requestBufferId, errorId, details, filename, madeBy, pageNumber, editIssueId)),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AddIssueForm);
